import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, ArrowLeft, Settings, Lock, Bell, User, Mail, 
  Trash2, Loader2, Eye, EyeOff, CheckCircle, Shield, LogOut,
  Smartphone, Moon, Sun
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function UserSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailMessages: true,
    emailOffers: true,
    emailWeeklyReport: true,
    pushNotifications: true
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  // Delete account
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setProfileData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || ''
      });
      // Load notification preferences if stored
      setNotifications({
        emailMessages: response.data.notifications?.emailMessages ?? true,
        emailOffers: response.data.notifications?.emailOffers ?? true,
        emailWeeklyReport: response.data.notifications?.emailWeeklyReport ?? true,
        pushNotifications: response.data.notifications?.pushNotifications ?? true
      });
    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        { withCredentials: true }
      );
      
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/profile`,
        { name: profileData.name, phone: profileData.phone },
        { withCredentials: true }
      );
      
      toast.success('Profile updated!');
      setUser(prev => ({ ...prev, name: profileData.name, phone: profileData.phone }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/notifications`,
        notifications,
        { withCredentials: true }
      );
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/users/account`, {
        withCredentials: true
      });
      toast.success('Account deleted');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.user_type) {
      case 'admin': return '/admin/dashboard';
      case 'employer': return '/employer/dashboard';
      case 'tradesperson': return '/tradesperson/dashboard';
      default: return '/';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'password', label: 'Password', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'danger', label: 'Danger Zone', icon: Trash2 }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-settings-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(getDashboardLink())}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900 font-heading uppercase tracking-tight">Settings</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:block">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar Tabs */}
          <div className="md:w-48 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-orange-100 text-orange-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className={`h-5 w-5 ${activeTab === tab.id ? 'text-orange-500' : ''}`} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1">
            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="card" data-testid="account-section">
                <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <User className="h-5 w-5 text-orange-500" />
                  Account Information
                </h2>
                
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full"
                      data-testid="name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={profileData.email}
                      disabled
                      className="w-full bg-slate-50 text-slate-500"
                      data-testid="email-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Email cannot be changed. Contact support if needed.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="+44 7XXX XXXXXX"
                      className="w-full"
                      data-testid="phone-input"
                    />
                  </div>
                  
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={savingProfile}
                      className="btn-primary flex items-center gap-2"
                      data-testid="save-profile-btn"
                    >
                      {savingProfile ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </form>
                
                {/* Account Info */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <h3 className="text-sm font-medium text-slate-700 mb-3">Account Details</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p><span className="font-medium">Account Type:</span> {user?.user_type?.charAt(0).toUpperCase() + user?.user_type?.slice(1)}</p>
                    <p><span className="font-medium">User ID:</span> {user?.user_id}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <div className="card" data-testid="password-section">
                <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Lock className="h-5 w-5 text-orange-500" />
                  Change Password
                </h2>
                
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        required
                        className="w-full pr-10"
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        required
                        minLength={6}
                        className="w-full pr-10"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        required
                        className="w-full pr-10"
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="btn-primary flex items-center gap-2"
                      data-testid="change-password-btn"
                    >
                      {changingPassword ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Shield className="h-4 w-4" />
                      )}
                      Change Password
                    </button>
                  </div>
                </form>
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Security Tip:</strong> Use a strong, unique password that you don't use for other accounts.
                  </p>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="card" data-testid="notifications-section">
                <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Bell className="h-5 w-5 text-orange-500" />
                  Notification Preferences
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div>
                      <p className="font-medium text-slate-900">New Messages</p>
                      <p className="text-sm text-slate-500">Get notified when you receive new messages</p>
                    </div>
                    <button
                      onClick={() => setNotifications({ ...notifications, emailMessages: !notifications.emailMessages })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        notifications.emailMessages ? 'bg-orange-500' : 'bg-slate-300'
                      }`}
                      data-testid="toggle-messages"
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        notifications.emailMessages ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div>
                      <p className="font-medium text-slate-900">Job Offers</p>
                      <p className="text-sm text-slate-500">Get notified about new job offers</p>
                    </div>
                    <button
                      onClick={() => setNotifications({ ...notifications, emailOffers: !notifications.emailOffers })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        notifications.emailOffers ? 'bg-orange-500' : 'bg-slate-300'
                      }`}
                      data-testid="toggle-offers"
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        notifications.emailOffers ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>
                  
                  {user?.user_type === 'employer' && (
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <div>
                        <p className="font-medium text-slate-900">Weekly Reports</p>
                        <p className="text-sm text-slate-500">Receive weekly activity summaries</p>
                      </div>
                      <button
                        onClick={() => setNotifications({ ...notifications, emailWeeklyReport: !notifications.emailWeeklyReport })}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          notifications.emailWeeklyReport ? 'bg-orange-500' : 'bg-slate-300'
                        }`}
                        data-testid="toggle-weekly"
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          notifications.emailWeeklyReport ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-slate-900">Push Notifications</p>
                      <p className="text-sm text-slate-500">Receive push notifications on your device</p>
                    </div>
                    <button
                      onClick={() => setNotifications({ ...notifications, pushNotifications: !notifications.pushNotifications })}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        notifications.pushNotifications ? 'bg-orange-500' : 'bg-slate-300'
                      }`}
                      data-testid="toggle-push"
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        notifications.pushNotifications ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>
                </div>
                
                <div className="pt-6">
                  <button
                    onClick={handleSaveNotifications}
                    disabled={savingNotifications}
                    className="btn-primary flex items-center gap-2"
                    data-testid="save-notifications-btn"
                  >
                    {savingNotifications ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Save Preferences
                  </button>
                </div>
              </div>
            )}

            {/* Danger Zone Tab */}
            {activeTab === 'danger' && (
              <div className="card border-red-200" data-testid="danger-section">
                <h2 className="text-lg font-bold text-red-600 mb-6 flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Danger Zone
                </h2>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-medium text-red-800 mb-2">Delete Account</h3>
                  <p className="text-sm text-red-700 mb-4">
                    Once you delete your account, there is no going back. All your data will be permanently removed.
                  </p>
                  <button
                    onClick={() => setShowDeleteDialog(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete My Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Account
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This action cannot be undone. All your data, messages, and history will be permanently deleted.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full"
                data-testid="delete-confirm-input"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
                className="btn-secondary flex-1"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Delete Forever'
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
